import { paths } from './../types';
import {
  UseQueryOptions,
  UseQueryResult,
  useQueries
} from '@tanstack/react-query';
import { FetchOptions, FetchResponse } from 'openapi-fetch';
import type {
  FilterKeys,
  PathsWithMethod,
  HasRequiredKeys,
  HttpMethod
} from 'openapi-typescript-helpers';
import getClient from './../client';

type QueryOptions<T> = {
  queryOpts?: Partial<
    Omit<
      UseQueryOptions<FetchResponse<T>['data'], FetchResponse<T>['error']>,
      'queryFn' | 'queryKey'
    >
  >;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type CreateUseTypedQueries<Paths extends {}> = {
  useTypedQueries<T extends HttpMethod, P extends PathsWithMethod<Paths, T>>(
    _method: T,
    _url: P,
    ..._options: HasRequiredKeys<
      FetchOptions<FilterKeys<Paths[P], T>>
    > extends never
      ? [
          (
            | (FetchOptions<FilterKeys<Paths[P], T>> &
                QueryOptions<T extends keyof Paths[P] ? Paths[P][T] : unknown>)
            | undefined
          )[]?
        ]
      : [
          (FetchOptions<FilterKeys<Paths[P], T>> &
            QueryOptions<T extends keyof Paths[P] ? Paths[P][T] : unknown>)[]
        ]
  ): UseQueryResult<
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['data'],
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['error']
  >[];
};

export const createUseTypedQueries: CreateUseTypedQueries<paths> = {
  useTypedQueries(method, url, ...options) {
    const _options = options[0];
    return useQueries({
      // @ts-expect-error this is ok
      queries: _options
        ? _options.map((option) => ({
            // @ts-expect-error Params does exist sometimes
            queryKey: [url, option?.body, option?.params],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const client = await getClient();
              // @ts-expect-error All good, we know this method exists
              const { data, error } = await client[method.toUpperCase()](url, {
                ...option,
                signal
              });
              if (error) throw new Error(error);
              return data;
            },
            ...option?.queryOpts
          }))
        : []
    });
  }
};

export const useTypedQueries = createUseTypedQueries.useTypedQueries;
